export type AudienceSeoIcon =
  | 'calendar'
  | 'file'
  | 'gift'
  | 'gem'
  | 'heart'
  | 'ruler'
  | 'shirt'
  | 'sparkles'
  | 'store'
  | 'users'
  | 'wand'

export type AudienceSeoLink = {
  label: string
  to: string
  description?: string
  eyebrow?: string
  icon?: AudienceSeoIcon
}

export type AudienceSeoPoint = {
  title: string
  text: string
  icon?: AudienceSeoIcon
}

export type AudienceSeoFaq = {
  question: string
  answer: string
}

export type AudienceSeoPage = {
  key: string
  locale: 'en' | 'fr'
  canonicalPath: string
  alternatePath?: string
  metaTitle: string
  metaDescription: string
  keywords: string
  breadcrumbLabel: string
  hero: {
    eyebrow: string
    title: string
    lead: string
    image: string
    imagePosition?: string
    imageAlt: string
    imageCaption: string
    stats: ReadonlyArray<{ label: string, value: string }>
    primaryCta: AudienceSeoLink
    secondaryCta?: AudienceSeoLink
  }
  audience: {
    label: string
    painTitle: string
    painText: string
    criteriaTitle: string
    criteria: ReadonlyArray<string>
  }
  sections: ReadonlyArray<{
    eyebrow: string
    title: string
    body: string
    points: ReadonlyArray<AudienceSeoPoint>
  }>
  process: {
    eyebrow: string
    title: string
    steps: ReadonlyArray<AudienceSeoPoint>
  }
  proof: {
    eyebrow: string
    title: string
    body: string
    image: string
    imageAlt: string
    links: ReadonlyArray<AudienceSeoLink>
  }
  cluster: {
    eyebrow: string
    title: string
    links: ReadonlyArray<AudienceSeoLink>
  }
  faq: ReadonlyArray<AudienceSeoFaq>
  finalCta: {
    eyebrow: string
    title: string
    body: string
    primaryCta: AudienceSeoLink
    secondaryCta?: AudienceSeoLink
  }
}

const privateAccessPath = (sourcePath: string, intentContext: string, locale: 'en' | 'fr') => {
  const homePath = locale === 'fr' ? '/fr' : '/'
  const query = new URLSearchParams({
    pa_source: sourcePath,
    pa_context: intentContext,
    pa_category: 'seo_landing'
  })

  return `${homePath}?${query.toString()}#private-access`
}

const enPrivateCta = (sourcePath: string, intentContext: string, label = 'Request private access') => ({
  label,
  to: privateAccessPath(sourcePath, intentContext, 'en'),
  icon: 'gem' as const
})

const frPrivateCta = (sourcePath: string, intentContext: string, label = 'Demander un accès privé') => ({
  label,
  to: privateAccessPath(sourcePath, intentContext, 'fr'),
  icon: 'gem' as const
})

const enLuxuryClusterLinks: ReadonlyArray<AudienceSeoLink> = [
  {
    eyebrow: 'Private access',
    label: 'Luxury heels for private clients',
    to: '/private-client-luxury-heels',
    description: 'For selected sizes, private occasions and occasional statement shoes.',
    icon: 'gem'
  },
  {
    eyebrow: 'Wedding',
    label: 'Luxury bridal heels',
    to: '/luxury-bridal-heels',
    description: 'Elegant heels for weddings, ceremonies and formal celebrations.',
    icon: 'calendar'
  },
  {
    eyebrow: 'Gift',
    label: 'Luxury heels as a gift',
    to: '/luxury-heels-gift',
    description: 'A high-value gift path for partners, anniversaries and milestones.',
    icon: 'gift'
  },
  {
    eyebrow: 'Professionals',
    label: 'Fashion stylist sourcing',
    to: '/professionals/fashion-stylists',
    description: 'Signature heels for editorials, fittings and curated client looks.',
    icon: 'shirt'
  },
  {
    eyebrow: 'Wholesale',
    label: 'Luxury boutique and retail buyers',
    to: '/professionals/wholesale-luxury-heels',
    description: 'Small-batch luxury footwear for selective retail partners.',
    icon: 'store'
  }
]

const frLuxuryClusterLinks: ReadonlyArray<AudienceSeoLink> = [
  {
    eyebrow: 'Accès privé',
    label: 'Chaussures luxe clientes privées',
    to: '/fr/chaussures-luxe-clientes-privees',
    description: 'Pour une silhouette distinctive, une occasion forte ou un achat accompagné.',
    icon: 'gem'
  },
  {
    eyebrow: 'Mariage',
    label: 'Chaussures de mariée luxe',
    to: '/fr/chaussures-mariee-luxe',
    description: 'Talons élégants pour robe, cérémonie et soirée de mariage.',
    icon: 'calendar'
  },
  {
    eyebrow: 'Cadeau',
    label: 'Cadeau chaussures luxe femme',
    to: '/fr/cadeau-chaussures-luxe-femme',
    description: 'Une demande privée comme cadeau marquant et personnel.',
    icon: 'gift'
  },
  {
    eyebrow: 'Professionnels',
    label: 'Stylistes mode',
    to: '/fr/professionnels/stylistes-mode',
    description: 'Sourcing chaussures pour éditorial, fitting et clients VIP.',
    icon: 'shirt'
  },
  {
    eyebrow: 'Boutiques',
    label: 'Boutiques chaussures luxe',
    to: '/fr/professionnels/boutiques-chaussures-luxe',
    description: 'Une ligne courte et distinctive pour distribution sélective.',
    icon: 'store'
  }
]

export const audienceSeoPages = {
  privateAccessLuxuryHeelsEn: {
    key: 'privateAccessLuxuryHeelsEn',
    locale: 'en',
    canonicalPath: '/private-access-luxury-heels',
    alternatePath: '/fr/chaussures-luxe-acces-prive',
    metaTitle: 'Private Access Luxury Heels | Kendrick Paris',
    metaDescription: 'Private access luxury heels by Kendrick Paris for private clients, bridal buyers, stylists and selective boutiques looking for rare, elegant statement silhouettes.',
    keywords: 'private access luxury heels, limited luxury heels, luxury heels Paris, designer heels, Kendrick Paris heels',
    breadcrumbLabel: 'Private access heels',
    hero: {
      eyebrow: 'Private access luxury heels',
      title: 'Signature heels for private wardrobes and selective professionals',
      lead: 'Kendrick creates Paris-designed, Italy-made heels for women who need a refined silhouette that feels personal, rare and strong enough for the moment.',
      image: '/brand-assets/kendrick/kendrick-boots-pumps-2000.jpg',
      imageAlt: 'Kendrick Paris black ankle boots and pumps in a luxury footwear composition',
      imageCaption: 'Paris design, Italian making, short luxury edits.',
      stats: [
        { label: 'Audience signals', value: '28K+' },
        { label: 'Core intents', value: '6' },
        { label: 'Distribution', value: 'Private + B2B' }
      ],
      primaryCta: enPrivateCta('/private-access-luxury-heels', 'private_access_luxury_heels'),
      secondaryCta: { label: 'View professional line sheet', to: '/professionals/wholesale-luxury-heels', icon: 'file' }
    },
    audience: {
      label: 'Who this is for',
      painTitle: 'Luxury heels are easy to find. The right pair is not.',
      painText: 'The strongest Kendrick searches come from people who already know the occasion, the wardrobe level and the need for something more precise than ordinary ready-to-wear.',
      criteriaTitle: 'Qualified buying signals',
      criteria: ['Planning a formal event or wardrobe moment', 'Seeking a refined silhouette with presence', 'Ready to invest in a rarer pair', 'Values exclusivity, fit and visual distinction']
    },
    sections: [
      {
        eyebrow: 'Private clients',
        title: 'A personal pair, not a generic luxury purchase',
        body: 'For occasional statement shoes, selected sizes and wardrobe-defining heels, Kendrick routes clients toward a private access conversation before availability, preorder or an appointment is confirmed.',
        points: [
          { title: 'Size clarity', text: 'Size, use context and silhouette preference are gathered early so the request can be reviewed before proposing a pair.', icon: 'ruler' },
          { title: 'Controlled choice', text: 'The edit stays short: pumps, ankle boots, boots and sandals with a clear Kendrick line.', icon: 'sparkles' },
          { title: 'Investment mindset', text: 'The page speaks to clients who buy fewer pieces and expect stronger identity from each one.', icon: 'gem' }
        ]
      },
      {
        eyebrow: 'Professionals',
        title: 'A sourcing path for stylists and selective retail',
        body: 'Stylists need pieces that elevate a look quickly. Boutique buyers need differentiation without overbuying inventory. The professional routes separate those needs from private-client conversion.',
        points: [
          { title: 'Styling impact', text: 'Editorial, VIP and bridal styling pages lead to a showroom and lookbook conversation.', icon: 'shirt' },
          { title: 'Retail restraint', text: 'The wholesale route focuses on small-batch product, short size runs and buyer proof.', icon: 'store' },
          { title: 'Press memory', text: 'The archive gives stylists and buyers a concrete way to understand the visual language.', icon: 'file' }
        ]
      }
    ],
    process: {
      eyebrow: 'How it works',
      title: 'From intent to the right Kendrick route',
      steps: [
        { title: 'Choose the context', text: 'Wedding, private wardrobe, gift, styling request or retail buying need.' },
        { title: 'Share fit and timing', text: 'Size, occasion, country, travel window and buying intent qualify the request.' },
        { title: 'Review the edit', text: 'Kendrick answers with the relevant private access, showroom or line sheet path.' },
        { title: 'Confirm feasibility', text: 'Availability, timing and terms are confirmed before any commitment.' }
      ]
    },
    proof: {
      eyebrow: 'Brand proof',
      title: 'A silhouette already readable in press and on camera',
      body: 'Kendrick has appeared in editorial and TV contexts where the shoe must carry presence quickly. That makes the brand easier to understand for private clients, stylists and retail buyers.',
      image: '/brand-assets/kendrick/kendrick-press-editorial-tv-collage.jpg',
      imageAlt: 'Kendrick Paris press and editorial collage',
      links: [
        { label: 'View press archive', to: '/press', icon: 'file' },
        { label: 'Explore the brand', to: '/brand', icon: 'sparkles' }
      ]
    },
    cluster: {
      eyebrow: 'SEO cocoon',
      title: 'Choose the route that matches the buying intent',
      links: enLuxuryClusterLinks
    },
    faq: [
      { question: 'What does private access mean for Kendrick heels?', answer: 'It is a qualified request path. Kendrick reviews size, use context, timing and availability before proposing the right route.' },
      { question: 'Does Kendrick make bridal heels?', answer: 'Kendrick supports bridal and formal-event requests when the client needs elegant heels that match a dress, ceremony and evening context.' },
      { question: 'Can stylists or boutiques contact Kendrick?', answer: 'Yes. Stylists should use the professional stylist page, while boutiques and retail buyers can request a wholesale line sheet and terms.' },
      { question: 'Where are Kendrick heels designed and made?', answer: 'Kendrick is designed in Paris and works with Italian making for its luxury footwear edits.' }
    ],
    finalCta: {
      eyebrow: 'Next step',
      title: 'Start with the strongest intent.',
      body: 'Private clients can request access. Professionals can move directly to the styling or wholesale route.',
      primaryCta: enPrivateCta('/private-access-luxury-heels', 'private_access_luxury_heels'),
      secondaryCta: { label: 'Professional routes', to: '/professionals/wholesale-luxury-heels', icon: 'store' }
    }
  },
  luxuryBridalHeelsEn: {
    key: 'luxuryBridalHeelsEn',
    locale: 'en',
    canonicalPath: '/luxury-bridal-heels',
    alternatePath: '/fr/chaussures-mariee-luxe',
    metaTitle: 'Luxury Bridal Heels for Weddings | Kendrick Paris',
    metaDescription: 'Elegant luxury bridal heels for weddings, ceremonies and formal celebrations. Kendrick Paris helps brides choose refined heels for the dress and the moment.',
    keywords: 'luxury bridal heels, wedding heels, designer bridal shoes, elegant heels for wedding, Kendrick Paris bridal',
    breadcrumbLabel: 'Luxury bridal heels',
    hero: {
      eyebrow: 'Bridal buyers',
      title: 'Luxury bridal heels for the dress, the ceremony and the long evening',
      lead: 'For brides and formal-event clients who need beautiful heels that match the outfit perfectly without losing the sharp Kendrick silhouette.',
      image: '/k/pumps/kendrick-pumps-beige-1.jpg',
      imagePosition: 'center bottom',
      imageAlt: 'Kendrick Paris light luxury pumps suitable for bridal styling',
      imageCaption: 'Ivory, nude and evening silhouettes can be reviewed through private access.',
      stats: [
        { label: 'Audience signal', value: '7.0K' },
        { label: 'Occasion', value: 'Wedding' },
        { label: 'Need', value: 'Elegant match' }
      ],
      primaryCta: enPrivateCta('/luxury-bridal-heels', 'bridal_heels', 'Plan a bridal request'),
      secondaryCta: { label: 'See pumps', to: '/pumps', icon: 'sparkles' }
    },
    audience: {
      label: 'Pain and criteria',
      painTitle: 'The shoe has to serve the whole look.',
      painText: 'A bridal heel is judged beside the dress, the venue, the ceremony rhythm and the photos. The wrong proportion can flatten the outfit even when the shoe is expensive.',
      criteriaTitle: 'Best-fit clients',
      criteria: ['Planning a wedding, civil ceremony or formal celebration', 'Looking for a refined heel silhouette', 'Needs ivory, nude, black or statement styling guidance', 'Ready to invest in a meaningful pair']
    },
    sections: [
      {
        eyebrow: 'Dress matching',
        title: 'A silhouette chosen around fabric, length and posture',
        body: 'The page answers the real bridal search: not simply "white shoes", but heels that work with satin, crepe, lace, tailoring, civil ceremony looks and evening dresses.',
        points: [
          { title: 'Color discipline', text: 'Ivory, nude, black and red are treated as styling decisions, not defaults.', icon: 'sparkles' },
          { title: 'Heel proportion', text: 'The height and cut need to work with the hemline and the way the bride stands.', icon: 'ruler' },
          { title: 'Ceremony timing', text: 'The request can include wedding date, fittings and delivery window.', icon: 'calendar' }
        ]
      },
      {
        eyebrow: 'Formal events',
        title: 'Beyond the wedding day',
        body: 'The same page also captures ceremony guests, civil wedding clients, rehearsal dinners and formal celebrations where the outfit requires a precise heel.',
        points: [
          { title: 'Civil ceremony', text: 'A clean pump or ankle boot can sharpen a tailored city wedding look.', icon: 'heart' },
          { title: 'Evening reception', text: 'The Kendrick line can carry a longer night-out silhouette after the ceremony.', icon: 'gem' },
          { title: 'Destination wedding', text: 'Travel timing and event location can be included in the private request.', icon: 'calendar' }
        ]
      }
    ],
    process: {
      eyebrow: 'Bridal process',
      title: 'What to share before a bridal review',
      steps: [
        { title: 'Event date', text: 'The wedding or ceremony timing determines whether a private request is realistic.' },
        { title: 'Dress direction', text: 'Share color, length, fabric and whether the look is formal, civil or evening-led.' },
        { title: 'Size and fit', text: 'Kendrick reviews size, preferred height and whether the shoe must support a long ceremony.' },
        { title: 'Private answer', text: 'The response can confirm the relevant silhouettes, timing and next step.' }
      ]
    },
    proof: {
      eyebrow: 'Styling proof',
      title: 'Shoes that read in a full silhouette',
      body: 'Kendrick is built for camera, posture and visual clarity. That matters on a wedding day, where every proportion is recorded.',
      image: '/magazine/kendrick-stiletto-sole.jpg',
      imageAlt: 'Kendrick stiletto sole detail for elegant formal styling',
      links: [
        { label: 'Wedding guest guide', to: '/fr/magazine/chaussures-mariage-invitee', icon: 'file' },
        { label: 'The heel as structure', to: '/magazine/heel-as-structure', icon: 'ruler' }
      ]
    },
    cluster: {
      eyebrow: 'Related intents',
      title: 'Continue through the bridal and private-client cocoon',
      links: [
        { label: 'Private access luxury heels', to: '/private-access-luxury-heels', description: 'The pillar page for private, bridal and professional routes.', icon: 'gem' },
        { label: 'Luxury heels for private clients', to: '/private-client-luxury-heels', description: 'For selected sizes and personal wardrobe moments.', icon: 'users' },
        { label: 'Luxury heels as a gift', to: '/luxury-heels-gift', description: 'For milestone gifts and guided private requests.', icon: 'gift' },
        { label: 'How Parisian women walk in heels', to: '/magazine/how-parisians-walk-in-heels', description: 'Posture guidance before a long formal event.', icon: 'ruler' }
      ]
    },
    faq: [
      { question: 'When should I request bridal heels?', answer: 'The earlier the better. A wedding request should include the event date and fitting timeline so Kendrick can answer realistically.' },
      { question: 'Can Kendrick help match shoes to a wedding dress?', answer: 'Kendrick can review the intended silhouette, color direction and event context through the private access route.' },
      { question: 'Are bridal heels only white or ivory?', answer: 'No. Nude, black, red and other statement tones can work depending on the dress, venue and styling direction.' },
      { question: 'Can the same pair work after the wedding?', answer: 'That is often the best luxury decision: a pair strong enough for the ceremony but wearable again with evening tailoring or formal looks.' }
    ],
    finalCta: {
      eyebrow: 'Bridal request',
      title: 'Share the date, dress direction and size.',
      body: 'Kendrick will review whether a private bridal request is possible and which silhouette makes sense.',
      primaryCta: enPrivateCta('/luxury-bridal-heels', 'bridal_heels', 'Plan a bridal request'),
      secondaryCta: { label: 'Explore pumps', to: '/pumps', icon: 'sparkles' }
    }
  },
  privateClientLuxuryHeelsEn: {
    key: 'privateClientLuxuryHeelsEn',
    locale: 'en',
    canonicalPath: '/private-client-luxury-heels',
    alternatePath: '/fr/chaussures-luxe-clientes-privees',
    metaTitle: 'Luxury Heels for Private Clients | Kendrick Paris',
    metaDescription: 'Request private access to Kendrick Paris luxury heels for private occasions, selected sizes and standout silhouettes.',
    keywords: 'private client luxury shoes, luxury heels for special occasion, private access heels, designer heels Paris, Kendrick Paris',
    breadcrumbLabel: 'Private clients',
    hero: {
      eyebrow: 'Private clients',
      title: 'Luxury heels for private clients, selected sizes and statement silhouettes',
      lead: 'For clients who already invest in a luxury wardrobe and want a guided answer rather than a generic product-grid experience.',
      image: '/images/preorder/kendrick-ankle-boots-black-21.jpg',
      imageAlt: 'Kendrick black ankle boots worn as a private evening statement shoe',
      imageCaption: 'Private requests prioritize fit, context and silhouette.',
      stats: [
        { label: 'Audience signal', value: '12.0K' },
        { label: 'Intent', value: 'Private access' },
        { label: 'Driver', value: 'Exclusivity' }
      ],
      primaryCta: enPrivateCta('/private-client-luxury-heels', 'private_client_heels', 'Request private access'),
      secondaryCta: { label: 'Understand the brand', to: '/brand', icon: 'sparkles' }
    },
    audience: {
      label: 'Pain and criteria',
      painTitle: 'Luxury can still feel impersonal.',
      painText: 'The private-client search is about control: a fit that works, a silhouette that feels rare, and a buying experience that respects the wardrobe level.',
      criteriaTitle: 'Best-fit clients',
      criteria: ['Luxury wardrobe spend', 'Values exclusivity and direct contact', 'Buys occasional statement shoes', 'May need a selected size, event pair or standout silhouette']
    },
    sections: [
      {
        eyebrow: 'Private selection',
        title: 'A request begins with the foot, the wardrobe and the occasion',
        body: 'Private clients do not need a broad catalog. They need to know whether the current or next Kendrick opening can answer the moment: size, black evening boot, formal pump, existing colorway or event timeline.',
        points: [
          { title: 'Size review', text: 'The private form captures size and country before the conversation moves forward.', icon: 'ruler' },
          { title: 'Occasion signal', text: 'Wedding, private event, Paris travel or wardrobe statement can all shape the answer.', icon: 'calendar' },
          { title: 'Short edit', text: 'A focused Kendrick edit avoids the noise of a generic luxury catalog.', icon: 'sparkles' }
        ]
      },
      {
        eyebrow: 'Exclusivity',
        title: 'The value is in the controlled route',
        body: 'A private request creates a higher-quality conversation than open checkout when availability, timing and client context matter.',
        points: [
          { title: 'Review before sale', text: 'Not every request should become an order. Kendrick can first decide whether the fit is right.', icon: 'gem' },
          { title: 'Founder-led context', text: 'The buyer receives a more precise answer than a public product page can provide.', icon: 'users' },
          { title: 'Wardrobe continuity', text: 'The pair should work beyond one look when the client invests at luxury level.', icon: 'heart' }
        ]
      }
    ],
    process: {
      eyebrow: 'Private process',
      title: 'A simple path from interest to review',
      steps: [
        { title: 'Request access', text: 'Share email, size, country, product interest and context.' },
        { title: 'Qualify the moment', text: 'Kendrick reviews event timing, use case and buying intent.' },
        { title: 'Receive a route', text: 'The answer may be a private opening, appointment, waitlist or not-now response.' },
        { title: 'Confirm next step', text: 'Availability, price band and timing are clarified before any commitment.' }
      ]
    },
    proof: {
      eyebrow: 'Private proof',
      title: 'Why I design shoes for women',
      body: 'This page connects leather, engineering, photography and the women observed through images to the black ankle boot, my signature model, before opening the next chapter of Kendrick more widely.',
      image: '/magazine/kendrick-ankle-boots-black.jpg',
      imageAlt: 'Kendrick black ankle boots worn on a white bed',
      links: [
        { label: 'Read the brand story', to: '/brand', icon: 'sparkles' },
        { label: 'See fabrication', to: '/fabrication', icon: 'file' }
      ]
    },
    cluster: {
      eyebrow: 'Related intents',
      title: 'Private-client search paths',
      links: [
        { label: 'Private access luxury heels', to: '/private-access-luxury-heels', description: 'The main page for all private and professional routes.', icon: 'gem' },
        { label: 'Luxury bridal heels', to: '/luxury-bridal-heels', description: 'For weddings, ceremonies and formal celebrations.', icon: 'calendar' },
        { label: 'Luxury heels as a gift', to: '/luxury-heels-gift', description: 'For milestone gifts and guided private requests.', icon: 'gift' },
        { label: 'The heel as structure', to: '/magazine/heel-as-structure', description: 'How Kendrick thinks about structure and posture.', icon: 'ruler' }
      ]
    },
    faq: [
      { question: 'Is private access a production promise?', answer: 'No. Private access qualifies size, model interest, timing and availability before suggesting an existing, planned or limited Kendrick silhouette.' },
      { question: 'Can I request a selected size?', answer: 'Yes. Size is captured early so Kendrick can evaluate whether a request is realistic.' },
      { question: 'Is private access the same as checkout?', answer: 'No. Private access is a qualified request before any confirmed order, preorder or availability promise.' },
      { question: 'What kinds of shoes can private clients request?', answer: 'Kendrick focuses on heels, ankle boots, boots, pumps and sandals with a strong Paris-designed silhouette.' }
    ],
    finalCta: {
      eyebrow: 'Private access',
      title: 'Ask for the pair that the market did not give you.',
      body: 'Share your size, context and desired silhouette so Kendrick can review the request.',
      primaryCta: enPrivateCta('/private-client-luxury-heels', 'private_client_heels'),
      secondaryCta: { label: 'View silhouettes', to: '/store', icon: 'sparkles' }
    }
  },
  luxuryHeelsGiftEn: {
    key: 'luxuryHeelsGiftEn',
    locale: 'en',
    canonicalPath: '/luxury-heels-gift',
    alternatePath: '/fr/cadeau-chaussures-luxe-femme',
    metaTitle: 'Luxury Heels Gift for Her | Kendrick Paris',
    metaDescription: 'Buy or choose luxury heels as a high-value gift for a partner, birthday, wedding milestone or meaningful celebration with Kendrick Paris.',
    keywords: 'luxury heels gift, luxury gift for her, designer shoes gift, private access heels gift, Kendrick Paris gift',
    breadcrumbLabel: 'Luxury heels gift',
    hero: {
      eyebrow: 'Gift purchasers',
      title: 'A luxury heels gift with personal meaning, not a generic box',
      lead: 'For partners, anniversaries and milestones where the gift needs prestige, rarity and a more memorable route than ordinary checkout.',
      image: '/k/pumps/kendrick-pumps-red-1.jpg',
      imageAlt: 'Kendrick Paris red pumps as a luxury gift idea',
      imageCaption: 'Gift requests can begin privately before choosing a silhouette.',
      stats: [
        { label: 'Audience signal', value: '5.0K' },
        { label: 'Use case', value: 'Milestone gift' },
        { label: 'Driver', value: 'Prestige' }
      ],
      primaryCta: enPrivateCta('/luxury-heels-gift', 'luxury_heels_gift', 'Discuss a gift'),
      secondaryCta: { label: 'See signature heels', to: '/pumps', icon: 'gift' }
    },
    audience: {
      label: 'Pain and criteria',
      painTitle: 'A meaningful gift needs more than a price tag.',
      painText: 'The buyer wants something personal and prestigious, but shoes require size, taste, timing and discretion. The page guides the gift toward a private request.',
      criteriaTitle: 'Best-fit buyers',
      criteria: ['High disposable income', 'Buying for a partner or milestone', 'Values exclusivity and emotional weight', 'Needs help choosing size, color or silhouette']
    },
    sections: [
      {
        eyebrow: 'Gift logic',
        title: 'The safest luxury gift is a guided private selection',
        body: 'A pair of heels can be unforgettable, but only if size, style and timing are handled carefully. Kendrick treats the gift as a private conversation first.',
        points: [
          { title: 'Occasion first', text: 'Birthday, wedding, anniversary and promotion gifts each need a different tone.', icon: 'calendar' },
          { title: 'Size sensitivity', text: 'The route can avoid guessing when the buyer is unsure about fit.', icon: 'ruler' },
          { title: 'Discretion', text: 'The request can stay private until the right route is clear.', icon: 'heart' }
        ]
      },
      {
        eyebrow: 'Memorability',
        title: 'A gift that becomes part of the wardrobe story',
        body: 'The best gift pair is not only worn once. It becomes a signature evening pair, a wedding memory or a visible object in a luxury wardrobe.',
        points: [
          { title: 'Statement color', text: 'Black, ivory, nude, red or yellow can each carry a different gift message.', icon: 'sparkles' },
          { title: 'Paris identity', text: 'Kendrick gives the gift a house story beyond the object.', icon: 'gem' },
          { title: 'Private answer', text: 'The response can suggest whether a gift route, appointment or private access makes sense.', icon: 'gift' }
        ]
      }
    ],
    process: {
      eyebrow: 'Gift process',
      title: 'How to begin without making the wrong choice',
      steps: [
        { title: 'Share the occasion', text: 'Milestone, date and relationship context help frame the request.' },
        { title: 'Share what you know', text: 'Size, usual brands, preferred colors or outfits can guide the answer.' },
        { title: 'Review options', text: 'Kendrick can suggest whether a specific pair, appointment or private route is appropriate.' },
        { title: 'Confirm discreetly', text: 'Only move forward when timing and fit are realistic.' }
      ]
    },
    proof: {
      eyebrow: 'Gift proof',
      title: 'A visible object with a stronger story',
      body: 'A Kendrick gift carries design, posture and Paris context. It is meant to feel chosen, not simply expensive.',
      image: '/brand-assets/kendrick-yellow-heels.jpg',
      imageAlt: 'Kendrick Paris yellow heels as a standout luxury gift silhouette',
      links: [
        { label: 'View pumps', to: '/pumps', icon: 'gift' },
        { label: 'Private clients', to: '/private-client-luxury-heels', icon: 'gem' }
      ]
    },
    cluster: {
      eyebrow: 'Related intents',
      title: 'Gift buyers often continue here',
      links: [
        { label: 'Luxury heels for private clients', to: '/private-client-luxury-heels', description: 'For selected sizes and more personal requests.', icon: 'gem' },
        { label: 'Luxury bridal heels', to: '/luxury-bridal-heels', description: 'For wedding and ceremony gifts.', icon: 'calendar' },
        { label: 'Private access luxury heels', to: '/private-access-luxury-heels', description: 'The full private and professional route map.', icon: 'sparkles' },
        { label: 'Contact Kendrick', to: '/contact', description: 'Use contact for a broader gift question.', icon: 'file' }
      ]
    },
    faq: [
      { question: 'Can I buy Kendrick heels as a gift?', answer: 'You can begin with a private request so Kendrick can review size, timing and the right silhouette before any gift route is confirmed.' },
      { question: 'What if I do not know her shoe size?', answer: 'Do not guess. Share what you know and Kendrick can advise whether a private conversation or appointment is more appropriate.' },
      { question: 'Are luxury heels a good wedding gift?', answer: 'They can be, especially when the pair is chosen around the ceremony, dress, travel and future wardrobe use.' },
      { question: 'Can the gift be discreet?', answer: 'Yes. The request route is designed to clarify feasibility before exposing details unnecessarily.' }
    ],
    finalCta: {
      eyebrow: 'Gift request',
      title: 'Make the gift specific before it becomes expensive.',
      body: 'Start with the occasion, timing and what you know about her style.',
      primaryCta: enPrivateCta('/luxury-heels-gift', 'luxury_heels_gift', 'Discuss a gift'),
      secondaryCta: { label: 'Explore pumps', to: '/pumps', icon: 'gift' }
    }
  },
  fashionStylistsEn: {
    key: 'fashionStylistsEn',
    locale: 'en',
    canonicalPath: '/professionals/fashion-stylists',
    alternatePath: '/fr/professionnels/stylistes-mode',
    metaTitle: 'Signature Heels for Fashion Stylists | Kendrick Paris',
    metaDescription: 'Source Kendrick Paris signature heels for editorials, fittings, celebrity styling, bridal styling and curated luxury client looks.',
    keywords: 'heels for fashion stylists, editorial shoes, stylist sourcing, luxury heels for fittings, Kendrick Paris stylists',
    breadcrumbLabel: 'Fashion stylists',
    hero: {
      eyebrow: 'Fashion stylists',
      title: 'Source signature heels for editorials, fittings and luxury client looks',
      lead: 'A professional route for stylists who need rare statement pieces that elevate a look quickly and photograph with a clear silhouette.',
      image: '/brand-assets/kendrick/kendrick-photography.jpg',
      imageAlt: 'Kendrick Paris footwear in fashion photography context',
      imageCaption: 'For independent stylists, celebrity wardrobe, editorials and bridal styling.',
      stats: [
        { label: 'Audience signal', value: '1.8K' },
        { label: 'Use case', value: 'Sourcing' },
        { label: 'Need', value: 'Rare pieces' }
      ],
      primaryCta: { label: 'Request stylist access', to: '/contact', icon: 'shirt' },
      secondaryCta: { label: 'View press archive', to: '/press', icon: 'file' }
    },
    audience: {
      label: 'Pain and criteria',
      painTitle: 'Styling needs a piece that changes the look immediately.',
      painText: 'The page speaks to professionals sourcing for luxury clients, press, shoots and fittings where ordinary footwear would make the silhouette feel unfinished.',
      criteriaTitle: 'Best-fit professionals',
      criteria: ['Works with luxury clients', 'Produces fashion-led looks', 'Needs rare statement pieces', 'Sources for editorials, celebrity wardrobes, bridal clients or personal shopping']
    },
    sections: [
      {
        eyebrow: 'Editorial',
        title: 'A heel that gives the frame a clearer line',
        body: 'For editorials and lookbooks, Kendrick provides angular silhouettes, color signals and shoe details that can hold their own in a full composition.',
        points: [
          { title: 'Camera readability', text: 'The shoe must be visible in movement, seated shots and cropped frames.', icon: 'sparkles' },
          { title: 'Color styling', text: 'Black, red, ivory and yellow options support precise mood direction.', icon: 'wand' },
          { title: 'Press context', text: 'Existing press references help a stylist explain why the piece belongs in the story.', icon: 'file' }
        ]
      },
      {
        eyebrow: 'Client styling',
        title: 'Distinctive footwear for high-touch wardrobes',
        body: 'Personal shoppers and celebrity wardrobe stylists can route requests around a client, event date and silhouette brief.',
        points: [
          { title: 'VIP fittings', text: 'Use the request to clarify size, timing and look direction before a fitting.', icon: 'users' },
          { title: 'Bridal styling', text: 'Formal and bridal stylists can connect directly to the wedding heel route.', icon: 'calendar' },
          { title: 'Luxury clienteling', text: 'Kendrick fits clients who want fewer, stronger statement objects.', icon: 'gem' }
        ]
      }
    ],
    process: {
      eyebrow: 'Stylist process',
      title: 'What to include in a professional sourcing request',
      steps: [
        { title: 'Project type', text: 'Editorial, celebrity wardrobe, client fitting, bridal styling or personal shopping.' },
        { title: 'Timing', text: 'Shoot date, fitting date, city and return or purchase constraints.' },
        { title: 'Look direction', text: 'Color, heel family, wardrobe references and required sizes.' },
        { title: 'Access route', text: 'Kendrick can answer with a press, showroom, private or purchase path.' }
      ]
    },
    proof: {
      eyebrow: 'Styling proof',
      title: 'Kendrick already belongs in fashion-led contexts',
      body: 'The archive gives stylists concrete visual proof: magazine context, TV appearances and statement color stories.',
      image: '/brand-assets/kendrick/kendrick-runway.jpg',
      imageAlt: 'Kendrick Paris fashion and runway context',
      links: [
        { label: 'Press archive', to: '/press', icon: 'file' },
        { label: 'Luxury bridal heels', to: '/luxury-bridal-heels', icon: 'calendar' }
      ]
    },
    cluster: {
      eyebrow: 'Professional cocoon',
      title: 'Related professional and private pages',
      links: [
        { label: 'Wholesale luxury heels', to: '/professionals/wholesale-luxury-heels', description: 'For boutiques, shoe salons and retail buyers.', icon: 'store' },
        { label: 'Luxury bridal heels', to: '/luxury-bridal-heels', description: 'For bridal stylists and ceremony looks.', icon: 'calendar' },
        { label: 'Private access luxury heels', to: '/private-access-luxury-heels', description: 'The full route map for private and professional intents.', icon: 'gem' },
        { label: 'Contact Kendrick', to: '/contact', description: 'Use this route for press, shoot or fitting requests.', icon: 'file' }
      ]
    },
    faq: [
      { question: 'Can stylists borrow Kendrick shoes for shoots?', answer: 'The page routes stylists to a professional request. Availability, terms and timing depend on the project and location.' },
      { question: 'What kinds of stylists is this for?', answer: 'Independent Paris stylists, celebrity wardrobe stylists, editorial stylists, luxury personal shoppers and bridal stylists.' },
      { question: 'Can Kendrick help with a bridal client?', answer: 'Yes. Bridal styling requests can use the bridal heel route or include the client context in a professional inquiry.' },
      { question: 'Where should a stylist request access?', answer: 'Use the contact route and include project type, timing, sizes and the visual direction.' }
    ],
    finalCta: {
      eyebrow: 'Professional access',
      title: 'Send the styling context before the look is locked.',
      body: 'Include the project, timing, sizes and the role the shoe needs to play.',
      primaryCta: { label: 'Request stylist access', to: '/contact', icon: 'shirt' },
      secondaryCta: { label: 'View press archive', to: '/press', icon: 'file' }
    }
  },
  wholesaleLuxuryHeelsEn: {
    key: 'wholesaleLuxuryHeelsEn',
    locale: 'en',
    canonicalPath: '/professionals/wholesale-luxury-heels',
    alternatePath: '/fr/professionnels/boutiques-chaussures-luxe',
    metaTitle: 'Wholesale Luxury Heels for Boutiques | Kendrick Paris',
    metaDescription: 'Wholesale luxury heels and small-batch footwear for boutiques, resort stores, luxury retailers and selective shoe salons by Kendrick Paris.',
    keywords: 'wholesale luxury heels, luxury footwear for boutiques, small-batch shoes, designer heels wholesale, Kendrick Paris wholesale',
    breadcrumbLabel: 'Wholesale luxury heels',
    hero: {
      eyebrow: 'Luxury boutiques and retailers',
      title: 'Small-batch luxury heels for selective boutiques and retail buyers',
      lead: 'A professional buying path for boutiques and luxury retailers that need fresh product, strong allure and controlled inventory.',
      image: '/brand-assets/kendrick/kendrick-boots-pumps-2000.jpg',
      imageAlt: 'Kendrick Paris wholesale footwear edit with boots and pumps',
      imageCaption: 'Short edits, buyer proof and pilot-size logic.',
      stats: [
        { label: 'Boutique signal', value: '1.4K' },
        { label: 'Retail signal', value: '900' },
        { label: 'Format', value: 'Pilot line sheet' }
      ],
      primaryCta: { label: 'Request line sheet', to: '/wholesales#order-request', icon: 'file' },
      secondaryCta: { label: 'View wholesale studio', to: '/wholesales', icon: 'store' }
    },
    audience: {
      label: 'Pain and criteria',
      painTitle: 'Retail needs differentiation without overbuying inventory.',
      painText: 'Boutiques and luxury retailers need a product with a strong story, a margin logic and enough rarity to make clienteling easier.',
      criteriaTitle: 'Best-fit buyers',
      criteria: ['Curated luxury assortment', 'Premium clientele or VIP clienteling', 'Small boutique footprint or selective retail edit', 'Needs fresh product without heavy inventory risk']
    },
    sections: [
      {
        eyebrow: 'Boutiques',
        title: 'A differentiated shoe line for high-touch clienteling',
        body: 'Kendrick gives a boutique a product that can be explained: Paris design, Italian making, posture, press memory and a silhouette that stands apart from ordinary accessory buying.',
        points: [
          { title: 'Small footprint', text: 'A short edit can fit a concept store, shoe salon or resort boutique without overloading the floor.', icon: 'store' },
          { title: 'Clienteling story', text: 'Staff can talk about posture, presence, press and the house instead of relying only on color.', icon: 'users' },
          { title: 'Local selectivity', text: 'Distribution can be reviewed city by city for stronger positioning.', icon: 'gem' }
        ]
      },
      {
        eyebrow: 'Retailers',
        title: 'A seasonal edit with a controlled first run',
        body: 'For retailers and corners, the key is a fresh luxury product that does not demand a broad assortment before sell-through signals exist.',
        points: [
          { title: 'Line sheet first', text: 'Buyers can review products, price band and size run logic before a full meeting.', icon: 'file' },
          { title: 'High-margin context', text: 'The page prepares a conversation around retail band, story and differentiation.', icon: 'gem' },
          { title: 'Pilot order logic', text: 'First runs can stay focused while the buyer tests client response.', icon: 'store' }
        ]
      }
    ],
    process: {
      eyebrow: 'Buyer process',
      title: 'From line sheet to first run',
      steps: [
        { title: 'Request line sheet', text: 'Share store, city, website, contact and buyer context.' },
        { title: 'Review product fit', text: 'Kendrick checks assortment, clientele and local distribution logic.' },
        { title: 'Discuss size run', text: 'The wholesale studio captures indicative sizes and total pairs for a pilot.' },
        { title: 'Confirm terms', text: 'Availability, minimums, margin and delivery window are confirmed directly.' }
      ]
    },
    proof: {
      eyebrow: 'Buyer proof',
      title: 'A product with visual memory before it reaches the floor',
      body: 'Kendrick can support a boutique story with press, TV and editorial references that make the line easier to introduce to a premium client.',
      image: '/brand-assets/kendrick/kendrick-premiere-classe.jpg',
      imageAlt: 'Kendrick Paris professional buyer and showroom context',
      links: [
        { label: 'Wholesale studio', to: '/wholesales', icon: 'file' },
        { label: 'Press archive', to: '/press', icon: 'sparkles' }
      ]
    },
    cluster: {
      eyebrow: 'B2B cocoon',
      title: 'Professional pages for buyers and stylists',
      links: [
        { label: 'Fashion stylist sourcing', to: '/professionals/fashion-stylists', description: 'For editorial, bridal and VIP styling requests.', icon: 'shirt' },
        { label: 'Wholesale studio', to: '/wholesales', description: 'The buyer line sheet and order request form.', icon: 'file' },
        { label: 'Private access luxury heels', to: '/private-access-luxury-heels', description: 'The pillar page connecting private and professional demand.', icon: 'gem' },
        { label: 'Kendrick press archive', to: '/press', description: 'Proof for buyer teams and clienteling.', icon: 'sparkles' }
      ]
    },
    faq: [
      { question: 'Does Kendrick offer wholesale luxury heels?', answer: 'Kendrick has a professional wholesale route for selected buyers, boutiques, shoe salons and retail partners.' },
      { question: 'What kinds of retailers fit Kendrick?', answer: 'Paris concept stores, Nice luxury boutiques, Lyon fashion boutiques, Monaco shoe salons, resort boutiques and selective multi-brand stores.' },
      { question: 'Can buyers request a small first run?', answer: 'The wholesale studio is designed around pilot logic and indicative size runs before any terms are confirmed.' },
      { question: 'Where can buyers request the line sheet?', answer: 'Use the wholesale studio form to request the line sheet, professional terms and buyer proof.' }
    ],
    finalCta: {
      eyebrow: 'Buyer access',
      title: 'Review the line before committing inventory.',
      body: 'Send store context, assortment and buyer timing. Kendrick will answer with the relevant professional route.',
      primaryCta: { label: 'Request line sheet', to: '/wholesales#order-request', icon: 'file' },
      secondaryCta: { label: 'Fashion stylist route', to: '/professionals/fashion-stylists', icon: 'shirt' }
    }
  },
  chaussuresLuxeAccesPriveFr: {
    key: 'chaussuresLuxeAccesPriveFr',
    locale: 'fr',
    canonicalPath: '/fr/chaussures-luxe-acces-prive',
    alternatePath: '/private-access-luxury-heels',
    metaTitle: 'Chaussures de luxe en accès privé | Kendrick Paris',
    metaDescription: 'Chaussures de luxe en accès privé Kendrick Paris pour clientes privées, mariées, stylistes et boutiques sélectives.',
    keywords: 'chaussures luxe accès privé, talons luxe Paris, chaussures créateur femme, talons édition limitée, Kendrick Paris',
    breadcrumbLabel: 'Chaussures luxe accès privé',
    hero: {
      eyebrow: 'Chaussures de luxe en accès privé',
      title: 'Des talons signature pour clientes privées et professionnels sélectifs',
      lead: 'Kendrick répond aux femmes, stylistes et acheteurs qui cherchent une silhouette rare, personnelle et suffisamment forte pour une occasion précise.',
      image: '/brand-assets/kendrick/kendrick-boots-pumps-2000.jpg',
      imageAlt: 'Bottines et escarpins Kendrick Paris dans une composition chaussures luxe',
      imageCaption: 'Design parisien, fabrication italienne, éditions courtes.',
      stats: [
        { label: 'Signaux audience', value: '28K+' },
        { label: 'Intentions', value: '6' },
        { label: 'Parcours', value: 'Privé + B2B' }
      ],
      primaryCta: frPrivateCta('/fr/chaussures-luxe-acces-prive', 'chaussures_luxe_acces_prive'),
      secondaryCta: { label: 'Voir les pages pro', to: '/fr/professionnels/boutiques-chaussures-luxe', icon: 'store' }
    },
    audience: {
      label: 'Pour qui',
      painTitle: 'Le luxe existe partout. La bonne paire, beaucoup moins.',
      painText: "Les recherches les plus qualifiées viennent de clientes et de professionnels qui connaissent déjà l'occasion, le niveau de garde-robe et le besoin d'une chaussure plus rare qu'un achat standard.",
      criteriaTitle: 'Signaux qualifiants',
      criteria: ['Événement formel ou moment de garde-robe', 'Recherche de silhouette raffinée', 'Envie de rareté et de contact direct', 'Budget cohérent avec une chaussure de créateur']
    },
    sections: [
      {
        eyebrow: 'Clientes privées',
        title: 'Une paire personnelle, pas un achat luxe générique',
        body: "Pour une taille recherchée, une occasion privée ou une silhouette forte, Kendrick dirige la demande vers l'accès privé avant de confirmer disponibilité, précommande ou rendez-vous.",
        points: [
          { title: 'Pointure et contexte', text: "La taille, l'usage et le calendrier sont recueillis avant de proposer une route.", icon: 'ruler' },
          { title: 'Édition courte', text: 'Escarpins, bottines, bottes et sandales restent dans une ligne Kendrick lisible.', icon: 'sparkles' },
          { title: 'Achat réfléchi', text: 'La page parle aux clientes qui achètent moins, mais veulent plus de présence.', icon: 'gem' }
        ]
      },
      {
        eyebrow: 'Professionnels',
        title: 'Un chemin clair pour stylistes et boutiques',
        body: 'Les stylistes cherchent une pièce qui élève immédiatement un look. Les boutiques cherchent une chaussure différenciante sans prendre trop de stock.',
        points: [
          { title: 'Styling', text: 'Le parcours stylistes prépare les demandes éditoriales, fittings, clientes VIP et mariées.', icon: 'shirt' },
          { title: 'Boutique', text: 'Le parcours wholesale parle de ligne courte, preuve acheteur et distribution sélective.', icon: 'store' },
          { title: 'Preuve visuelle', text: "Les parutions donnent un contexte utile avant l'achat ou le sourcing.", icon: 'file' }
        ]
      }
    ],
    process: {
      eyebrow: 'Méthode',
      title: "De l'intention au bon parcours Kendrick",
      steps: [
        { title: "Qualifier l'occasion", text: 'Mariage, cliente privée, cadeau, stylisme ou demande boutique.' },
        { title: 'Partager taille et timing', text: 'Pointure, pays, date, usage et intention d’achat orientent la réponse.' },
        { title: 'Recevoir la bonne route', text: 'Accès privé, contact styliste, line sheet ou échange direct selon le besoin.' },
        { title: 'Confirmer la faisabilité', text: 'Disponibilité, prix, délai et conditions sont clarifiés avant tout engagement.' }
      ]
    },
    proof: {
      eyebrow: 'Preuve de marque',
      title: "Une silhouette déjà lisible dans la presse et à l'écran",
      body: 'Kendrick a déjà vécu dans des contextes éditoriaux et télévisés où la chaussure doit porter une présence immédiate.',
      image: '/brand-assets/kendrick/kendrick-press-editorial-tv-collage.jpg',
      imageAlt: 'Collage presse et éditorial Kendrick Paris',
      links: [
        { label: 'Voir les parutions', to: '/fr/press', icon: 'file' },
        { label: 'Comprendre la maison', to: '/fr/brand', icon: 'sparkles' }
      ]
    },
    cluster: {
      eyebrow: 'Cocon SEO',
      title: "Choisir la page selon l'intention d'achat",
      links: frLuxuryClusterLinks
    },
    faq: [
      { question: "À quoi sert l'accès privé Kendrick ?", answer: "L'accès privé sert à qualifier taille, occasion, modèle souhaité, timing et disponibilité avant d'orienter vers une silhouette Kendrick existante, prévue ou limitée." },
      { question: 'Quelle page choisir pour un mariage ?', answer: 'La page chaussures de mariée luxe répond aux demandes de robe, cérémonie, mairie, cocktail et soirée.' },
      { question: 'Les stylistes et boutiques peuvent-ils contacter Kendrick ?', answer: 'Oui. Les stylistes ont une page professionnelle dédiée, et les boutiques peuvent demander la ligne et les conditions B2B.' },
      { question: 'Où les chaussures Kendrick sont-elles conçues ?', answer: 'Kendrick est une maison parisienne qui travaille ses éditions de chaussures de luxe avec une fabrication italienne.' }
    ],
    finalCta: {
      eyebrow: 'Prochaine étape',
      title: "Commencer par l'intention la plus claire.",
      body: 'Cliente privée, mariée, cadeau, styliste ou boutique : chaque demande a maintenant son parcours.',
      primaryCta: frPrivateCta('/fr/chaussures-luxe-acces-prive', 'chaussures_luxe_acces_prive'),
      secondaryCta: { label: 'Pages professionnelles', to: '/fr/professionnels/boutiques-chaussures-luxe', icon: 'store' }
    }
  },
  chaussuresMarieeLuxeFr: {
    key: 'chaussuresMarieeLuxeFr',
    locale: 'fr',
    canonicalPath: '/fr/chaussures-mariee-luxe',
    alternatePath: '/luxury-bridal-heels',
    metaTitle: 'Chaussures de mariée luxe | Kendrick Paris',
    metaDescription: 'Chaussures de mariée luxe et talons élégants pour mariage, cérémonie civile, cocktail et soirée. Demandez un accès privé Kendrick Paris.',
    keywords: 'chaussures mariée luxe, talons mariage luxe, escarpins mariée, chaussures cérémonie femme, Kendrick Paris mariage',
    breadcrumbLabel: 'Chaussures de mariée luxe',
    hero: {
      eyebrow: 'Mariage et cérémonie',
      title: 'Chaussures de mariée luxe pour la robe, la cérémonie et la soirée',
      lead: 'Pour les mariées et clientes cérémonie qui veulent une paire élégante, cohérente avec la tenue et suffisamment forte pour rester dans la garde-robe.',
      image: '/k/pumps/kendrick-pumps-beige-1.jpg',
      imagePosition: 'center bottom',
      imageAlt: 'Escarpins clairs Kendrick Paris adaptés au styling mariage',
      imageCaption: 'Ivoire, nude, noir ou couleur statement selon la robe et le moment.',
      stats: [
        { label: 'Signal audience', value: '7.0K' },
        { label: 'Moment', value: 'Mariage' },
        { label: 'Besoin', value: 'Accord parfait' }
      ],
      primaryCta: frPrivateCta('/fr/chaussures-mariee-luxe', 'chaussures_mariee_luxe', 'Préparer une demande mariage'),
      secondaryCta: { label: 'Voir les escarpins', to: '/fr/escarpins', icon: 'sparkles' }
    },
    audience: {
      label: 'Douleur et critères',
      painTitle: 'La chaussure doit servir toute la silhouette.',
      painText: 'Une chaussure de mariée se juge avec la robe, le lieu, la posture, les photos et la durée de la journée. Le luxe seul ne suffit pas.',
      criteriaTitle: 'Clientes concernées',
      criteria: ['Mariage, cérémonie civile, cocktail ou soirée formelle', 'Recherche de silhouette raffinée', 'Besoin de conseil couleur ou proportion', 'Volonté d’investir dans une paire durable']
    },
    sections: [
      {
        eyebrow: 'Accord robe',
        title: 'Choisir selon la matière, la longueur et la posture',
        body: 'La page répond à la vraie recherche mariage : pas seulement une chaussure blanche, mais une paire qui fonctionne avec satin, crêpe, dentelle, tailleur civil ou robe du soir.',
        points: [
          { title: 'Couleur', text: 'Ivoire, nude, noir, rouge ou autre couleur doivent être traités comme choix de style.', icon: 'sparkles' },
          { title: 'Proportion', text: 'La hauteur et la ligne du talon doivent dialoguer avec la robe et la démarche.', icon: 'ruler' },
          { title: 'Calendrier', text: 'Date du mariage, essayages et livraison doivent être partagés tôt.', icon: 'calendar' }
        ]
      },
      {
        eyebrow: 'Au-delà du jour J',
        title: 'Une paire qui peut vivre après le mariage',
        body: 'La meilleure paire luxe fonctionne aussi après la cérémonie : dîner, événement, tenue tailleur ou garde-robe de soirée.',
        points: [
          { title: 'Mairie', text: 'Un escarpin ou une bottine nette peut renforcer un look civil urbain.', icon: 'heart' },
          { title: 'Soirée', text: 'La ligne Kendrick garde sa présence dans un contexte cocktail ou hôtel.', icon: 'gem' },
          { title: 'Destination', text: 'Le pays, le voyage et la date orientent la faisabilité.', icon: 'calendar' }
        ]
      }
    ],
    process: {
      eyebrow: 'Process mariage',
      title: 'Ce qu’il faut partager avant une réponse',
      steps: [
        { title: 'Date et lieu', text: 'Ils déterminent le délai réel et le contexte de port.' },
        { title: 'Direction robe', text: 'Couleur, longueur, matière et niveau de formalité aident à orienter la silhouette.' },
        { title: 'Pointure et confort', text: 'La demande doit préciser pointure, hauteur préférée et durée de port.' },
        { title: 'Réponse privée', text: 'Kendrick indique la route possible, les silhouettes et le prochain échange.' }
      ]
    },
    proof: {
      eyebrow: 'Preuve styling',
      title: 'Des chaussures pensées pour une silhouette complète',
      body: 'Kendrick travaille la posture et la présence, deux critères essentiels pour une journée photographiée et longue.',
      image: '/magazine/kendrick-stiletto-sole.jpg',
      imageAlt: 'Détail semelle stiletto Kendrick pour silhouette de cérémonie',
      links: [
        { label: 'Guide invitée mariage', to: '/fr/magazine/chaussures-mariage-invitee', icon: 'file' },
        { label: 'Le talon comme structure', to: '/fr/magazine/talon-comme-structure', icon: 'ruler' }
      ]
    },
    cluster: {
      eyebrow: 'Liens mariage',
      title: 'Continuer dans le cocon mariage et privé',
      links: [
        { label: 'Chaussures luxe accès privé', to: '/fr/chaussures-luxe-acces-prive', description: 'La page pilier pour les demandes privées et professionnelles.', icon: 'gem' },
        { label: 'Chaussures luxe clientes privées', to: '/fr/chaussures-luxe-clientes-privees', description: 'Pour une taille recherchée ou une silhouette distinctive.', icon: 'users' },
        { label: 'Cadeau chaussures luxe femme', to: '/fr/cadeau-chaussures-luxe-femme', description: 'Pour offrir une demande privée.', icon: 'gift' },
        { label: 'Chaussures mariage invitée', to: '/fr/magazine/chaussures-mariage-invitee', description: 'Guide éditorial pour invitées et cérémonies.', icon: 'file' }
      ]
    },
    faq: [
      { question: 'Quand demander des chaussures de mariée luxe ?', answer: 'Le plus tôt possible, avec la date, les essayages et la direction de robe.' },
      { question: 'Faut-il choisir absolument du blanc ou de l’ivoire ?', answer: 'Non. Nude, noir, rouge ou couleur statement peuvent être justes selon la robe et la cérémonie.' },
      { question: 'Kendrick peut-elle aider à choisir la silhouette ?', answer: 'Oui, via une demande privée qui précise robe, pointure, date et contexte d’usage.' },
      { question: 'La paire peut-elle être portée après le mariage ?', answer: "C'est souvent l'objectif : une paire assez forte pour le mariage, mais encore pertinente après." }
    ],
    finalCta: {
      eyebrow: 'Demande mariage',
      title: 'Partager la date, la robe et la pointure.',
      body: 'Kendrick pourra indiquer si une demande mariage privée est réaliste.',
      primaryCta: frPrivateCta('/fr/chaussures-mariee-luxe', 'chaussures_mariee_luxe', 'Préparer une demande mariage'),
      secondaryCta: { label: 'Voir les escarpins', to: '/fr/escarpins', icon: 'sparkles' }
    }
  },
  chaussuresLuxeClientesPriveesFr: {
    key: 'chaussuresLuxeClientesPriveesFr',
    locale: 'fr',
    canonicalPath: '/fr/chaussures-luxe-clientes-privees',
    alternatePath: '/private-client-luxury-heels',
    metaTitle: 'Chaussures luxe pour clientes privées | Kendrick Paris',
    metaDescription: 'Demande d’accès privé Kendrick Paris pour clientes privées, occasions spéciales, tailles recherchées et silhouettes fortes.',
    keywords: 'chaussures luxe clientes privées, talons luxe femme, chaussures créateur occasion spéciale, accès privé chaussures, Kendrick Paris',
    breadcrumbLabel: 'Clientes privées',
    hero: {
      eyebrow: 'Clientes privées',
      title: 'Chaussures luxe pour clientes privées, tailles recherchées et silhouettes statement',
      lead: "Pour les clientes qui investissent déjà dans une garde-robe luxe et veulent une réponse accompagnée plutôt qu'une recherche boutique standard.",
      image: '/images/preorder/kendrick-ankle-boots-black-21.jpg',
      imageAlt: 'Bottines noires Kendrick portées comme chaussure statement de soirée',
      imageCaption: 'La demande privée priorise pointure, contexte et silhouette.',
      stats: [
        { label: 'Signal audience', value: '12.0K' },
        { label: 'Intention', value: 'Commande privée' },
        { label: 'Moteur', value: 'Exclusivité' }
      ],
      primaryCta: frPrivateCta('/fr/chaussures-luxe-clientes-privees', 'chaussures_luxe_clientes_privees'),
      secondaryCta: { label: 'Comprendre la maison', to: '/fr/brand', icon: 'sparkles' }
    },
    audience: {
      label: 'Douleur et critères',
      painTitle: 'Même le luxe peut sembler impersonnel.',
      painText: 'La cliente privée cherche le contrôle : une pointure, une silhouette rare, un échange direct et une paire qui correspond vraiment au moment.',
      criteriaTitle: 'Clientes concernées',
      criteria: ['Budget garde-robe luxe', 'Recherche d’exclusivité', 'Achats occasionnels mais forts', 'Pointure rare, événement privé ou silhouette statement']
    },
    sections: [
      {
        eyebrow: 'Sélection privée',
        title: "La demande commence par le pied, l'usage et l'occasion",
        body: "La cliente n'a pas besoin d'un catalogue large. Elle a besoin de savoir si l'ouverture Kendrick actuelle ou prochaine peut répondre à une taille, un délai, une bottine du soir, un escarpin ou un coloris déjà dans la ligne.",
        points: [
          { title: 'Revue de taille', text: 'La pointure est recueillie dès le départ pour qualifier la demande.', icon: 'ruler' },
          { title: 'Contexte', text: 'Mariage, événement privé, voyage à Paris ou garde-robe de soirée orientent la réponse.', icon: 'calendar' },
          { title: 'Édition courte', text: 'Kendrick garde une ligne claire au lieu d’un catalogue impersonnel.', icon: 'sparkles' }
        ]
      },
      {
        eyebrow: 'Exclusivité',
        title: 'La valeur tient aussi au parcours contrôlé',
        body: "Un accès privé crée une meilleure conversation qu'un checkout ouvert lorsque disponibilité, timing et faisabilité comptent.",
        points: [
          { title: 'Revue avant vente', text: 'Toute demande ne doit pas devenir une commande. Kendrick vérifie d’abord la pertinence.', icon: 'gem' },
          { title: 'Réponse directe', text: 'La cliente obtient un contexte plus précis qu’une fiche produit publique.', icon: 'users' },
          { title: 'Continuité', text: 'La paire doit vivre au-delà d’un seul look quand l’achat est luxe.', icon: 'heart' }
        ]
      }
    ],
    process: {
      eyebrow: 'Process privé',
      title: "De l'intérêt à la revue",
      steps: [
        { title: 'Demander l’accès', text: 'Email, pointure, pays, produit souhaité et contexte.' },
        { title: 'Qualifier le moment', text: 'Kendrick examine timing, usage et intention d’achat.' },
        { title: 'Recevoir une route', text: 'Ouverture privée, rendez-vous, liste d’attente ou réponse différée.' },
        { title: 'Confirmer la suite', text: 'Disponibilité, prix et délai sont clarifiés avant engagement.' }
      ]
    },
    proof: {
      eyebrow: 'Preuve privée',
      title: 'Pourquoi je crée des chaussures pour femmes',
      body: 'Cette page relie le cuir, l’ingénierie, la photo et les femmes observées à l’image à la bottine noire, mon modèle emblématique, avant d’ouvrir plus largement le prochain chapitre de Kendrick.',
      image: '/magazine/kendrick-ankle-boots-black.jpg',
      imageAlt: 'Bottines noires Kendrick portées sur un lit blanc',
      links: [
        { label: 'Lire la maison', to: '/fr/brand', icon: 'sparkles' },
        { label: 'Voir la fabrication', to: '/fr/fabrication', icon: 'file' }
      ]
    },
    cluster: {
      eyebrow: 'Demandes liées',
      title: 'Les recherches clientes privées continuent ici',
      links: [
        { label: 'Chaussures luxe accès privé', to: '/fr/chaussures-luxe-acces-prive', description: 'La page pilier de tous les parcours.', icon: 'gem' },
        { label: 'Chaussures de mariée luxe', to: '/fr/chaussures-mariee-luxe', description: 'Pour mariage, cérémonie et soirée.', icon: 'calendar' },
        { label: 'Cadeau chaussures luxe femme', to: '/fr/cadeau-chaussures-luxe-femme', description: 'Pour offrir une paire avec plus de sens.', icon: 'gift' },
        { label: 'Le talon comme structure', to: '/fr/magazine/talon-comme-structure', description: 'Comprendre la structure et la posture Kendrick.', icon: 'ruler' }
      ]
    },
    faq: [
      { question: 'Comment fonctionne une demande privée ?', answer: "La demande privée sert à vérifier pointure, modèle souhaité, délai et disponibilité avant d'orienter vers une silhouette Kendrick existante, prévue ou limitée." },
      { question: 'Puis-je demander une pointure rare ?', answer: 'Oui. La pointure fait partie des informations essentielles de la demande privée.' },
      { question: 'L’accès privé est-il une commande automatique ?', answer: 'Non. C’est une demande qualifiée avant confirmation de disponibilité, production ou prix.' },
      { question: 'Quels modèles sont concernés ?', answer: 'Kendrick travaille les talons, bottines, bottes, escarpins et sandales avec une ligne parisienne forte.' }
    ],
    finalCta: {
      eyebrow: 'Accès privé',
      title: 'Demander la paire que le marché ne vous donne pas.',
      body: 'Partagez pointure, contexte et silhouette souhaitée pour permettre une revue Kendrick.',
      primaryCta: frPrivateCta('/fr/chaussures-luxe-clientes-privees', 'chaussures_luxe_clientes_privees'),
      secondaryCta: { label: 'Voir les silhouettes', to: '/fr/store', icon: 'sparkles' }
    }
  },
  cadeauChaussuresLuxeFemmeFr: {
    key: 'cadeauChaussuresLuxeFemmeFr',
    locale: 'fr',
    canonicalPath: '/fr/cadeau-chaussures-luxe-femme',
    alternatePath: '/luxury-heels-gift',
    metaTitle: 'Cadeau chaussures luxe femme | Kendrick Paris',
    metaDescription: 'Offrir des chaussures de luxe femme avec un accès privé Kendrick Paris pour anniversaire, mariage, partenaire ou moment important.',
    keywords: 'cadeau chaussures luxe femme, cadeau luxe femme, offrir escarpins luxe, cadeau chaussures créateur, Kendrick Paris cadeau',
    breadcrumbLabel: 'Cadeau chaussures luxe femme',
    hero: {
      eyebrow: 'Cadeau luxe',
      title: 'Un cadeau chaussures luxe femme qui semble vraiment choisi',
      lead: 'Pour un partenaire, un anniversaire, un mariage ou une étape importante, la paire doit avoir du prestige, mais aussi du sens.',
      image: '/k/pumps/kendrick-pumps-red-1.jpg',
      imageAlt: 'Escarpins rouges Kendrick Paris comme idée cadeau luxe femme',
      imageCaption: 'La demande cadeau peut commencer en privé avant de choisir la silhouette.',
      stats: [
        { label: 'Signal audience', value: '5.0K' },
        { label: 'Usage', value: 'Cadeau marquant' },
        { label: 'Moteur', value: 'Prestige' }
      ],
      primaryCta: frPrivateCta('/fr/cadeau-chaussures-luxe-femme', 'cadeau_chaussures_luxe', 'Discuter un cadeau'),
      secondaryCta: { label: 'Voir les escarpins', to: '/fr/escarpins', icon: 'gift' }
    },
    audience: {
      label: 'Douleur et critères',
      painTitle: 'Un cadeau important ne peut pas seulement être cher.',
      painText: 'Le cadeau doit être personnel, prestigieux et juste. Mais une chaussure demande pointure, goût, délai et discrétion.',
      criteriaTitle: 'Acheteurs concernés',
      criteria: ['Fort pouvoir d’achat', 'Cadeau pour partenaire ou milestone', 'Valeur émotionnelle et exclusivité', 'Besoin d’aide sur taille, couleur ou silhouette']
    },
    sections: [
      {
        eyebrow: 'Logique cadeau',
        title: 'Le cadeau le plus sûr commence par une demande guidée',
        body: 'Une paire de talons peut être inoubliable si la taille, le style et le timing sont traités avec soin.',
        points: [
          { title: 'Occasion', text: 'Anniversaire, mariage, promotion ou étape personnelle appellent des tons différents.', icon: 'calendar' },
          { title: 'Pointure', text: 'Mieux vaut éviter de deviner quand la taille n’est pas certaine.', icon: 'ruler' },
          { title: 'Discrétion', text: 'Le parcours peut rester privé jusqu’à ce que la bonne route soit claire.', icon: 'heart' }
        ]
      },
      {
        eyebrow: 'Mémoire',
        title: 'Un cadeau qui entre dans l’histoire de la garde-robe',
        body: "La bonne paire n'est pas seulement portée une fois. Elle devient une chaussure de soirée, un souvenir de mariage ou un objet visible dans la garde-robe.",
        points: [
          { title: 'Couleur statement', text: 'Noir, ivoire, nude, rouge ou jaune portent chacun un message différent.', icon: 'sparkles' },
          { title: 'Identité Paris', text: 'Kendrick apporte une histoire de maison au-delà de l’objet.', icon: 'gem' },
          { title: 'Réponse privée', text: 'La demande permet de savoir si cadeau, rendez-vous ou accès privé est pertinent.', icon: 'gift' }
        ]
      }
    ],
    process: {
      eyebrow: 'Process cadeau',
      title: 'Commencer sans faire le mauvais choix',
      steps: [
        { title: 'Partager l’occasion', text: 'Date, relation, surprise et contexte du cadeau.' },
        { title: 'Partager les indices', text: 'Pointure, marques portées, couleurs, tenues ou contraintes.' },
        { title: 'Étudier les options', text: 'Kendrick peut orienter vers une paire, un rendez-vous ou un accès privé.' },
        { title: 'Confirmer discrètement', text: 'La suite se décide seulement si taille et timing sont réalistes.' }
      ]
    },
    proof: {
      eyebrow: 'Preuve cadeau',
      title: 'Un objet visible avec une histoire plus forte',
      body: 'Un cadeau Kendrick porte design, posture et contexte parisien. Il doit sembler choisi, pas seulement acheté.',
      image: '/brand-assets/kendrick-yellow-heels.jpg',
      imageAlt: 'Talons jaunes Kendrick Paris comme silhouette cadeau statement',
      links: [
        { label: 'Voir les escarpins', to: '/fr/escarpins', icon: 'gift' },
        { label: 'Clientes privées', to: '/fr/chaussures-luxe-clientes-privees', icon: 'gem' }
      ]
    },
    cluster: {
      eyebrow: 'Demandes liées',
      title: 'Les acheteurs cadeau continuent souvent ici',
      links: [
        { label: 'Chaussures luxe clientes privées', to: '/fr/chaussures-luxe-clientes-privees', description: 'Pour une demande plus personnelle.', icon: 'gem' },
        { label: 'Chaussures de mariée luxe', to: '/fr/chaussures-mariee-luxe', description: 'Pour cadeau de mariage ou cérémonie.', icon: 'calendar' },
        { label: 'Chaussures luxe accès privé', to: '/fr/chaussures-luxe-acces-prive', description: 'Le parcours complet des demandes privées.', icon: 'sparkles' },
        { label: 'Contact Kendrick', to: '/fr/contact', description: 'Pour une question cadeau plus ouverte.', icon: 'file' }
      ]
    },
    faq: [
      { question: 'Peut-on offrir des chaussures Kendrick ?', answer: 'Oui, en commençant par une demande privée pour vérifier taille, timing et silhouette avant toute décision.' },
      { question: 'Et si je ne connais pas sa pointure ?', answer: 'Il vaut mieux ne pas deviner. Partagez les indices disponibles et Kendrick indiquera si une route cadeau est possible.' },
      { question: 'Est-ce une bonne idée cadeau mariage ?', answer: 'Oui si la paire est pensée avec la robe, la cérémonie et une utilisation future.' },
      { question: 'La demande peut-elle rester discrète ?', answer: 'Oui. Le parcours clarifie la faisabilité avant d’exposer inutilement les détails.' }
    ],
    finalCta: {
      eyebrow: 'Demande cadeau',
      title: 'Rendre le cadeau précis avant de le rendre coûteux.',
      body: "Commencez par l'occasion, le timing et les indices de style.",
      primaryCta: frPrivateCta('/fr/cadeau-chaussures-luxe-femme', 'cadeau_chaussures_luxe', 'Discuter un cadeau'),
      secondaryCta: { label: 'Voir les escarpins', to: '/fr/escarpins', icon: 'gift' }
    }
  },
  stylistesModeFr: {
    key: 'stylistesModeFr',
    locale: 'fr',
    canonicalPath: '/fr/professionnels/stylistes-mode',
    alternatePath: '/professionals/fashion-stylists',
    metaTitle: 'Chaussures pour stylistes mode | Kendrick Paris',
    metaDescription: 'Sourcing Kendrick Paris pour stylistes mode, éditoriaux, fittings, clientes VIP, stylistes célébrités et stylistes mariage.',
    keywords: 'chaussures stylistes mode, sourcing chaussures éditorial, chaussures luxe fitting, stylistes célébrités, Kendrick Paris stylistes',
    breadcrumbLabel: 'Stylistes mode',
    hero: {
      eyebrow: 'Stylistes mode',
      title: 'Sourcer des talons signature pour éditoriaux, fittings et clientes luxe',
      lead: 'Un parcours professionnel pour les stylistes qui cherchent des pièces rares, visuelles et capables d’élever rapidement une silhouette.',
      image: '/brand-assets/kendrick/kendrick-photography.jpg',
      imageAlt: 'Chaussures Kendrick Paris dans un contexte photographie mode',
      imageCaption: 'Pour stylistes indépendants, célébrités, éditoriaux, personal shoppers et mariages.',
      stats: [
        { label: 'Signal audience', value: '1.8K' },
        { label: 'Usage', value: 'Sourcing' },
        { label: 'Besoin', value: 'Pièces rares' }
      ],
      primaryCta: { label: 'Demander un accès styliste', to: '/fr/contact', icon: 'shirt' },
      secondaryCta: { label: 'Voir les parutions', to: '/fr/press', icon: 'file' }
    },
    audience: {
      label: 'Douleur et critères',
      painTitle: 'Le styling a besoin d’une pièce qui change le look immédiatement.',
      painText: 'La page parle aux professionnels qui sourcent pour clientes luxe, presse, shootings et fittings où une chaussure ordinaire affaiblirait la silhouette.',
      criteriaTitle: 'Professionnels concernés',
      criteria: ['Travaille avec clientes luxe', 'Produit des looks mode ou éditoriaux', 'A besoin de pièces statement rares', 'Sourcing éditorial, célébrités, mariées ou personal shopping']
    },
    sections: [
      {
        eyebrow: 'Éditorial',
        title: 'Un talon qui clarifie la ligne dans l’image',
        body: 'Pour éditoriaux et lookbooks, Kendrick apporte des silhouettes angulaires, des couleurs signal et des détails qui tiennent dans une composition complète.',
        points: [
          { title: 'Lisibilité caméra', text: 'La chaussure doit rester visible en mouvement, assise ou dans un cadrage serré.', icon: 'sparkles' },
          { title: 'Couleurs', text: 'Noir, rouge, ivoire et jaune soutiennent des directions de mood précises.', icon: 'wand' },
          { title: 'Contexte presse', text: 'Les références existantes aident à justifier la pièce dans l’histoire.', icon: 'file' }
        ]
      },
      {
        eyebrow: 'Client styling',
        title: 'Chaussures distinctives pour garde-robes très accompagnées',
        body: 'Personal shoppers et stylistes célébrités peuvent orienter leur demande autour d’une cliente, d’un événement et d’un brief silhouette.',
        points: [
          { title: 'Fittings VIP', text: 'Clarifier pointure, timing et direction avant un fitting.', icon: 'users' },
          { title: 'Styling mariage', text: 'Les stylistes mariage peuvent se relier au parcours chaussures de mariée luxe.', icon: 'calendar' },
          { title: 'Clienteling luxe', text: 'Kendrick convient aux clientes qui veulent moins de pièces mais plus fortes.', icon: 'gem' }
        ]
      }
    ],
    process: {
      eyebrow: 'Process styliste',
      title: 'Ce qu’il faut inclure dans une demande de sourcing',
      steps: [
        { title: 'Type de projet', text: 'Éditorial, célébrité, fitting, mariée ou personal shopping.' },
        { title: 'Timing', text: 'Date de shoot, fitting, ville et contraintes de retour ou achat.' },
        { title: 'Direction look', text: 'Couleur, famille de talon, références de tenue et pointures.' },
        { title: 'Route d’accès', text: 'Kendrick répond avec une route presse, showroom, privée ou achat.' }
      ]
    },
    proof: {
      eyebrow: 'Preuve styling',
      title: 'Kendrick existe déjà dans des contextes mode',
      body: 'Les archives donnent aux stylistes une preuve visuelle concrète : presse, télévision, couleur et silhouette.',
      image: '/brand-assets/kendrick/kendrick-runway.jpg',
      imageAlt: 'Contexte mode et runway Kendrick Paris',
      links: [
        { label: 'Voir les parutions', to: '/fr/press', icon: 'file' },
        { label: 'Chaussures mariée luxe', to: '/fr/chaussures-mariee-luxe', icon: 'calendar' }
      ]
    },
    cluster: {
      eyebrow: 'Cocon pro',
      title: 'Pages professionnelles et privées liées',
      links: [
        { label: 'Boutiques chaussures luxe', to: '/fr/professionnels/boutiques-chaussures-luxe', description: 'Pour boutiques, salons chaussures et acheteurs.', icon: 'store' },
        { label: 'Chaussures de mariée luxe', to: '/fr/chaussures-mariee-luxe', description: 'Pour stylistes mariage et looks cérémonie.', icon: 'calendar' },
        { label: 'Chaussures luxe accès privé', to: '/fr/chaussures-luxe-acces-prive', description: 'La page pilier des intentions privées et professionnelles.', icon: 'gem' },
        { label: 'Contact Kendrick', to: '/fr/contact', description: 'Pour demandes presse, shoot ou fitting.', icon: 'file' }
      ]
    },
    faq: [
      { question: 'Les stylistes peuvent-ils demander des chaussures Kendrick ?', answer: 'Oui. La demande doit préciser projet, date, pointures et direction visuelle.' },
      { question: 'Quels stylistes sont concernés ?', answer: 'Stylistes indépendants, stylistes célébrités, éditoriaux, personal shoppers luxe et stylistes mariage.' },
      { question: 'Kendrick peut-elle aider pour une cliente mariée ?', answer: 'Oui, via le parcours chaussures de mariée luxe ou une demande professionnelle avec le contexte cliente.' },
      { question: 'Où envoyer une demande styliste ?', answer: 'Utilisez la page contact en précisant projet, timing, pointures et objectif du look.' }
    ],
    finalCta: {
      eyebrow: 'Accès professionnel',
      title: 'Envoyer le contexte avant de verrouiller le look.',
      body: 'Précisez projet, timing, pointures et rôle de la chaussure.',
      primaryCta: { label: 'Demander un accès styliste', to: '/fr/contact', icon: 'shirt' },
      secondaryCta: { label: 'Voir les parutions', to: '/fr/press', icon: 'file' }
    }
  },
  boutiquesChaussuresLuxeFr: {
    key: 'boutiquesChaussuresLuxeFr',
    locale: 'fr',
    canonicalPath: '/fr/professionnels/boutiques-chaussures-luxe',
    alternatePath: '/professionals/wholesale-luxury-heels',
    metaTitle: 'Chaussures luxe pour boutiques | Kendrick Paris',
    metaDescription: 'Chaussures luxe en wholesale pour boutiques, concept-stores, salons chaussures et acheteurs retail sélectifs. Demandez la ligne Kendrick Paris.',
    keywords: 'chaussures luxe boutiques, wholesale chaussures luxe, chaussures créateur boutiques, ligne chaussures boutique, Kendrick Paris B2B',
    breadcrumbLabel: 'Boutiques chaussures luxe',
    hero: {
      eyebrow: 'Boutiques et acheteurs luxe',
      title: 'Une ligne courte de chaussures luxe pour boutiques sélectives',
      lead: 'Un parcours acheteur pour boutiques, salons chaussures et retailers qui cherchent un produit différenciant, désirable et contrôlé en stock.',
      image: '/brand-assets/kendrick/kendrick-boots-pumps-2000.jpg',
      imageAlt: 'Édition wholesale Kendrick Paris avec bottines et escarpins',
      imageCaption: 'Éditions courtes, preuve acheteur et logique pilote.',
      stats: [
        { label: 'Signal boutiques', value: '1.4K' },
        { label: 'Signal retailers', value: '900' },
        { label: 'Format', value: 'Line sheet pilote' }
      ],
      primaryCta: { label: 'Demander la ligne', to: '/fr/professionnels/boutiques-multimarques-luxe#demande-b2b', icon: 'file' },
      secondaryCta: { label: 'Page B2B détaillée', to: '/fr/professionnels/boutiques-multimarques-luxe', icon: 'store' }
    },
    audience: {
      label: 'Douleur et critères',
      painTitle: 'Une boutique doit se différencier sans surstocker.',
      painText: "Les acheteurs ont besoin d'un produit à histoire forte, marge cohérente et rareté suffisante pour faciliter le clienteling.",
      criteriaTitle: 'Acheteurs concernés',
      criteria: ['Assortiment luxe curaté', 'Clientèle premium ou VIP', 'Surface boutique courte ou edit retail sélectif', 'Besoin de nouveauté sans risque stock excessif']
    },
    sections: [
      {
        eyebrow: 'Boutiques',
        title: 'Une chaussure de créateur facile à raconter en boutique',
        body: 'Kendrick donne à une boutique un produit explicable : design parisien, fabrication italienne, posture, presse et silhouette différenciante.',
        points: [
          { title: 'Petite surface', text: 'Une édition courte peut vivre dans un concept-store, salon chaussure ou resort boutique.', icon: 'store' },
          { title: 'Clienteling', text: 'Les équipes peuvent parler de posture, présence, presse et maison.', icon: 'users' },
          { title: 'Sélectivité locale', text: 'La distribution peut être étudiée ville par ville.', icon: 'gem' }
        ]
      },
      {
        eyebrow: 'Retail',
        title: 'Un edit saisonnier avant un engagement large',
        body: "Pour retailers et corners, l'enjeu est d'introduire un produit luxe frais sans exiger un assortiment trop large avant les signaux de vente.",
        points: [
          { title: 'Line sheet', text: 'L’acheteur peut étudier produits, prix et logique de taille avant un rendez-vous complet.', icon: 'file' },
          { title: 'Marge et histoire', text: 'La page prépare la conversation autour prix retail, différenciation et désirabilité.', icon: 'gem' },
          { title: 'Commande pilote', text: 'La première sélection peut rester courte pour tester la réponse cliente.', icon: 'store' }
        ]
      }
    ],
    process: {
      eyebrow: 'Process acheteur',
      title: 'De la ligne au premier run',
      steps: [
        { title: 'Demander la ligne', text: 'Boutique, ville, site, contact et contexte acheteur.' },
        { title: 'Vérifier le fit', text: 'Kendrick étudie assortiment, clientèle et logique de distribution.' },
        { title: 'Discuter les tailles', text: 'La demande B2B peut préparer un premier run ou un rendez-vous.' },
        { title: 'Confirmer les conditions', text: 'Disponibilités, minimums, marge et délais sont vus directement.' }
      ]
    },
    proof: {
      eyebrow: 'Preuve acheteur',
      title: 'Un produit avec mémoire visuelle avant la mise en rayon',
      body: 'Kendrick peut soutenir une histoire boutique avec presse, télévision et références éditoriales.',
      image: '/brand-assets/kendrick/kendrick-premiere-classe.jpg',
      imageAlt: 'Contexte professionnel et showroom Kendrick Paris',
      links: [
        { label: 'Page B2B détaillée', to: '/fr/professionnels/boutiques-multimarques-luxe', icon: 'file' },
        { label: 'Parutions', to: '/fr/press', icon: 'sparkles' }
      ]
    },
    cluster: {
      eyebrow: 'Cocon B2B',
      title: 'Pages professionnelles liées',
      links: [
        { label: 'Stylistes mode', to: '/fr/professionnels/stylistes-mode', description: 'Pour éditoriaux, clientes VIP et fittings.', icon: 'shirt' },
        { label: 'Page B2B détaillée', to: '/fr/professionnels/boutiques-multimarques-luxe', description: 'Formulaire boutiques et conditions professionnelles.', icon: 'file' },
        { label: 'Chaussures luxe accès privé', to: '/fr/chaussures-luxe-acces-prive', description: 'La page pilier reliant privé et professionnel.', icon: 'gem' },
        { label: 'Parutions Kendrick', to: '/fr/press', description: 'Preuve pour équipes boutique et clienteling.', icon: 'sparkles' }
      ]
    },
    faq: [
      { question: 'Kendrick propose-t-elle du wholesale ?', answer: 'Oui, pour boutiques, salons chaussures et acheteurs sélectionnés, après qualification professionnelle.' },
      { question: 'Quelles boutiques correspondent à Kendrick ?', answer: 'Concept-stores parisiens, boutiques luxe à Nice, Lyon, Monaco, resort boutiques et multi-marques sélectifs.' },
      { question: 'Peut-on commencer par une petite sélection ?', answer: 'Oui. Le parcours B2B privilégie une logique pilote et une édition courte avant tout engagement large.' },
      { question: 'Où demander les conditions ?', answer: 'La demande se fait via la page professionnelle boutiques multimarques luxe.' }
    ],
    finalCta: {
      eyebrow: 'Accès acheteur',
      title: 'Étudier la ligne avant de prendre du stock.',
      body: "Envoyez le contexte boutique, l'assortiment et le calendrier d'achat.",
      primaryCta: { label: 'Demander la ligne', to: '/fr/professionnels/boutiques-multimarques-luxe#demande-b2b', icon: 'file' },
      secondaryCta: { label: 'Route stylistes', to: '/fr/professionnels/stylistes-mode', icon: 'shirt' }
    }
  }
} as const satisfies Record<string, AudienceSeoPage>

export type AudienceSeoPageKey = keyof typeof audienceSeoPages

export const getAudienceSeoPage = (key: AudienceSeoPageKey): AudienceSeoPage => audienceSeoPages[key]
